import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  AllSteps,
  Answers,
  Scenario,
  ScenarioStepModifiableElements,
  ScenarioType,
  StepId,
  Steps,
  Translatable,
} from './interfaces'

interface State {
  current: Scenario | null
  loading: boolean
  waitingForAnswer: boolean
  additionalInformation: Record<string, unknown>
  originalScenarioType: ScenarioType
  scenarioCookie: Record<string, unknown>
  scenarioScenarioType: string
}

const normalizeAdditionalInformationValue = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return value.replace(/\s+/g, ' ').trim()
  }

  if (Array.isArray(value)) {
    return value.map(normalizeAdditionalInformationValue)
  }

  if (value !== null && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const normalizedValue: Record<string, unknown> = {}
    const valueAsRecord = value as Record<string, unknown>

    for (const key in valueAsRecord) {
      normalizedValue[key] = normalizeAdditionalInformationValue(valueAsRecord[key])
    }

    return normalizedValue
  }

  return value
}

export const normalizeScenarioAdditionalInformation = (
  additionalInformation: Record<string, unknown>,
): Record<string, unknown> => {
  const normalizedAdditionalInformation: Record<string, unknown> = {}

  for (const key in additionalInformation) {
    normalizedAdditionalInformation[key] = normalizeAdditionalInformationValue(additionalInformation[key])
  }

  return normalizedAdditionalInformation
}

export const scenarioSlice = createSlice({
  name: 'scenario',
  initialState: { current: null, loading: false } as State,
  reducers: {
    initScenario: (state) => {
      state.loading = true
    },
    setIsScenarioLoading: (state, action: PayloadAction<boolean | undefined>) => {
      state.loading = action?.payload === undefined ? true : action.payload
    },
    setIsScenarioWaitingForAnswer: (state, action: PayloadAction<boolean>) => {
      state.waitingForAnswer = action.payload
    },
    setScenario: (state, action: PayloadAction<Scenario>) => {
      state.current = action.payload
    },
    setOriginalScenarioType: (state, action: PayloadAction<ScenarioType>) => {
      state.originalScenarioType = action.payload
    },
    updateScenario: (state, action: PayloadAction<{ stepToUpdate: Steps; value: unknown; element: string }>) => {
      // TODO: Find a proper place for this code and refactor
      const scenario: Scenario | null = state.current
      const steps = scenario?.steps
      steps?.forEach((step) => {
        if (step.id === action.payload?.stepToUpdate) {
          if (action.payload?.element === ScenarioStepModifiableElements.QUESTION) {
            step.question = action.payload?.value as Translatable<`${AllSteps}_question`>
          }
          if (action.payload?.element === ScenarioStepModifiableElements.NEXT) {
            step.next = action.payload?.value as StepId
          }
          if (action.payload?.element === ScenarioStepModifiableElements.ANSWER_PLACEHOLDER) {
            step.answerPlaceholder = action.payload?.value as Translatable<`${AllSteps}_answerPlaceholder`>
          }
        }
      })
    },
    setScenarioAdditionalInformation: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.additionalInformation = normalizeScenarioAdditionalInformation(action.payload)
    },
    mergeScenarioAdditionalInformation: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.additionalInformation = normalizeScenarioAdditionalInformation({
        ...(state.additionalInformation || {}),
        ...action.payload,
      })
    },
    setScenarioCookie: (state, action: PayloadAction<Record<string, unknown>>) => {
      state.scenarioCookie = action.payload
    },
    setScenarioScenarioType: (state, action: PayloadAction<string>) => {
      state.scenarioScenarioType = action.payload
    },
    startScenario: (state, action: PayloadAction<Scenario>) => {
      state.current = action.payload
      state.loading = false
    },
  },
})

export const restartScenario = createAction('scenario/restartScenario')
export const finishScenario = createAction<Answers | undefined>('scenario/finishScenario')
export const {
  initScenario,
  setScenario,
  setOriginalScenarioType,
  setIsScenarioLoading,
  setIsScenarioWaitingForAnswer,
  startScenario,
  updateScenario,
  setScenarioAdditionalInformation,
  mergeScenarioAdditionalInformation,
  setScenarioCookie,
  setScenarioScenarioType,
} = scenarioSlice.actions
